import { OkrMethodologyFacade } from "./../../goals/components/facades/okr-methodology.facade";
import { IComponentOptions, IPromise, IScope, IQService } from "angular";
import { IModalService } from "angular-ui-bootstrap";
import { HierarchyNode, stratify } from "d3";
import dayjs from "@gtmhub/dayjs";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { IModalComponent } from "@gtmhub/core/routing";
import { IPlanningSession } from "@gtmhub/sessions/models/models";
import { INgRedux } from "@gtmhub/state-management";
import { PlanningSessionsActions } from "@gtmhub/sessions/redux/session-actions";
import { GtmhubController } from "@gtmhub/core";
import { toIdMap } from "@gtmhub/util";
import { WhiteboardStateHolder } from "../rendering/state/whiteboard-state-holder";
import { IGoalState, IDraftGoalState, isDraftOrImportedGoalState } from "../rendering/shapes";
import { GoalService } from "@gtmhub/goals/goal.service";
import { IIndicator } from "@gtmhub/error-handling";
import { IBulkCreateGoalRequest } from "@gtmhub/goals/models";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { ShapeStateConverter } from "../rendering/state/shape-state-converter";
import { localize } from "@gtmhub/localization";
import { WhiteboardEvents } from "../events";
import { DraftToImportedGoalMap } from "../models";
import { ISessionsStoreState } from "@gtmhub/sessions";
import { IAssignee, IAssigneesStoreState, respectsAssignPermissions } from "@gtmhub/assignees";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { AssigneeService } from "@gtmhub/assignees/services";
import { PlanningSessionService } from "@gtmhub/sessions";
import binocularsurl from "wwwroot/img/icons/binoculars.svg";

interface IGoalData {
  id: string;
  goal: IGoalState;
  session: IPlanningSession;
  imported: boolean;
  error?: string;
  selected?: boolean;
  published?: boolean;
  metricsExceedLimit?: boolean;
  goalLimitReached?: boolean;
}

interface ISessionsStats {
  sessionId: string;
  ownerIds: string[];
  owners?: IOwnersStats[];
}

interface IOwnersStats {
  ownerId: string;
  goalsCount: number;
  draftGoalsCount: number;
}

const rootNode = { id: "root" } as IGoalData;

const noSessionId = "none";

const bySessionEndDateDesc = (a: HierarchyNode<IGoalData>[], b: HierarchyNode<IGoalData>[]): number => {
  const aSession = a[0].data.session;
  const bSession = b[0].data.session;

  if (aSession.id === bSession.id) {
    return 0;
  }

  // no-session goes last
  if (aSession.id === noSessionId) {
    return 1;
  }
  if (bSession.id === noSessionId) {
    return -1;
  }

  // sort by end date, desc
  return dayjs(bSession.end).diff(aSession.end);
};

const removeGoalLeaves = (tree: HierarchyNode<IGoalData>, filters: ((data: HierarchyNode<IGoalData>) => boolean)[]) => {
  let childRemoved: boolean;
  do {
    childRemoved = false;
    for (const leaf of tree.leaves()) {
      if (filters.some((filter) => filter(leaf))) {
        leaf.parent.children = leaf.parent.children.filter((c) => c !== leaf);
        childRemoved = true;
      }
    }
  } while (childRemoved);
};

const nonDraft = (leaf: HierarchyNode<IGoalData>) => leaf.depth > 0 && leaf.data.imported;
const withNoName = (leaf: HierarchyNode<IGoalData>) => leaf.data?.goal?.name === "";

interface IPublishDraftsComponentRedux {
  sessions: Record<string, IPlanningSession>;
  assignees: Record<string, IAssignee>;
}

const mapStateToCtrl = (state: ISessionsStoreState & IAssigneesStoreState): IPublishDraftsComponentRedux => {
  return {
    sessions: toIdMap(state.sessions.items),
    assignees: state.assignees.map,
  };
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IPublishDraftsComponentBindings extends IModalComponent<{ whiteboardId: string }> {}

export interface PublishDraftsComponentCtrl extends IPublishDraftsComponentBindings, IPublishDraftsComponentRedux {}
export class PublishDraftsComponentCtrl extends GtmhubController {
  indicators: {
    loading?: IIndicator;
    publishing?: IIndicator;
    validating?: IIndicator;
  };
  cycle?: boolean;
  goalsPerSessionTrees: HierarchyNode<IGoalData>[][];
  published: boolean;
  goalsLimitReached?: boolean;
  noDraftOkrsErrorIcon: string;

  private goalsTree: HierarchyNode<IGoalData>;
  private noSession: IPlanningSession;
  private events: WhiteboardEvents;
  private sessionsStats: ISessionsStats[] = [];

  private readonly narrowedDialogCssClass = "narrower-dialog";

  static $inject = [
    "$scope",
    "$ngRedux",
    "PlanningSessionsActions",
    "WhiteboardStateHolder",
    "ShapeStateConverter",
    "GoalService",
    "AnalyticsService",
    "$rootScope",
    "AssigneeService",
    "PlanningSessionService",
    "UIErrorHandlingService",
    "$q",
    "OkrMethodologyFacade",
  ];

  constructor(
    $scope: IScope,
    $ngRedux: INgRedux,
    planningSessionsActions: PlanningSessionsActions,
    private whiteboardStateHolder: WhiteboardStateHolder,
    private shapeStateConverter: ShapeStateConverter,
    private goalService: GoalService,
    private analyticsService: AnalyticsService,
    private $rootScope: ITraceRootScopeService,
    private assigneeService: AssigneeService,
    private planningSessionService: PlanningSessionService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private $q: IQService,
    private okrMethodologyFacade: OkrMethodologyFacade
  ) {
    super();

    this.onDestroy($ngRedux.connect(mapStateToCtrl)(this));
    $ngRedux.dispatch(planningSessionsActions.getSessionsIfMissing());

    this.goalsPerSessionTrees = [];
    this.indicators = {};
    this.noSession = { id: noSessionId, title: localize("no_planning_session") };
    this.events = new WhiteboardEvents($scope);
    this.noDraftOkrsErrorIcon = binocularsurl;
  }

  get hasDraftGoals(): boolean {
    return this.goalsPerSessionTrees.length > 0;
  }

  $onInit(): void {
    try {
      this.createGoalsPerSessionTrees();
      this.toggleNarrowedModal(!this.hasDraftGoals);
    } catch (error) {
      if ((error as Error).message === "cycle") {
        this.cycle = true;
      } else {
        this.indicators.loading = { error };
      }
    }
  }

  toggleNarrowedModal(narrowed: boolean): void {
    setTimeout(() => {
      const modal = document.querySelector(".publish-drafts .modal-dialog");

      if (narrowed) {
        modal.classList.add(this.narrowedDialogCssClass);
      }
    });
  }

  canSelectNode(node: HierarchyNode<IGoalData>): boolean {
    return node.data !== rootNode && !node.data.error && !node.data.imported;
  }

  toggle(node: HierarchyNode<IGoalData>): void {
    if (!this.canSelectNode(node)) {
      return;
    }

    node.data.selected = !node.data.selected;

    // select all parents
    if (node.data.selected) {
      let parent = node.parent;
      while (parent && parent.data !== rootNode) {
        if (this.canSelectNode(parent)) {
          parent.data.selected = true;
        }
        parent = parent.parent;
      }
    } else {
      // unselect all children
      for (const child of node.descendants()) {
        if (this.canSelectNode(child)) {
          child.data.selected = false;
        }
      }
    }

    this.invalidateGoalLimitReached();
    this.syncDraftGoalsCountStats();
    this.checkAllGoalsLimitReached();
  }

  publish(): void {
    this.$rootScope.traceAction("publish_draft_goals", () => {
      this.indicators.publishing = { progress: true };
      this.publishLevel(1, {});
    });
  }

  close(): void {
    if (this.published) {
      this.close();
    } else {
      this.dismiss();
    }
  }

  private publishLevel(depth: number, results: DraftToImportedGoalMap) {
    let hasMoreDepths = false;
    const selectedNodes: HierarchyNode<IGoalData>[] = [];
    const bulkCreateGoals: IBulkCreateGoalRequest[] = [];

    for (const node of this.goalsTree) {
      if (node.depth < depth) {
        continue;
      }

      if (node.depth > depth) {
        hasMoreDepths = true;
        break;
      }

      if (node.data.selected) {
        selectedNodes.push(node);

        const goalRequest = this.createBulkGoalRequest(node.data.id);
        bulkCreateGoals.push(goalRequest);

        // if we have already published a goal, we need to potentially update the parent IDs of the subsequence request
        if (goalRequest.parentId) {
          const draftToImportedParentsMap: Record<string, string> =
            goalRequest.parentType === "metric"
              ? Object.values(results).reduce((map, importedGoal) => Object.assign(map, importedGoal.metrics), {})
              : Object.entries(results).reduce((map, [draftGoalId, importedGoal]) => Object.assign(map, { [draftGoalId]: importedGoal.id }), {});

          if (draftToImportedParentsMap[goalRequest.parentId]) {
            goalRequest.parentId = draftToImportedParentsMap[goalRequest.parentId];
          }
        }
      }
    }

    const publishInnerDepth = () => {
      if (hasMoreDepths) {
        this.publishLevel(depth + 1, results);
      } else {
        delete this.indicators.publishing;
        this.broadcastDraftsPublished(results);
      }
    };

    if (!bulkCreateGoals.length) {
      publishInnerDepth();
      return;
    }

    this.goalService.createBulkGoals(bulkCreateGoals).then(
      (bulkCreateGoalResults) => {
        this.analyticsService.track("Draft OKRs published from whiteboard", { whiteboard_id: this.resolve.whiteboardId, okrs: bulkCreateGoalResults.length });

        this.published = true;

        for (let i = 0; i < bulkCreateGoalResults.length; i++) {
          const bulkCreatedGoal = bulkCreateGoalResults[i];
          const node = selectedNodes[i];

          const draftGoal = this.whiteboardStateHolder.findShape<IDraftGoalState>(node.data.id);
          results[draftGoal.id] = {
            id: bulkCreatedGoal.id,
            metrics:
              bulkCreatedGoal.metrics && bulkCreatedGoal.metrics.length === draftGoal.metrics.length
                ? draftGoal.metrics.reduce((map, metric, index) => Object.assign(map, { [metric.id]: bulkCreatedGoal.metrics[index].id }), {})
                : {},
          };

          node.data.id = bulkCreatedGoal.id;
          node.data.selected = false;
          node.data.published = true;
          node.data.imported = true;
        }

        publishInnerDepth();
      },
      (error) => {
        this.indicators.publishing = { error };
        this.broadcastDraftsPublished(results);
      }
    );
  }

  private broadcastDraftsPublished(results: DraftToImportedGoalMap) {
    if (Object.keys(results).length) {
      this.events.broadcastDraftsPublished(results);
    }
  }

  private createGoalsPerSessionTrees() {
    const goals = this.whiteboardStateHolder.findShapes(isDraftOrImportedGoalState) as IGoalState[];
    const goalMap = toIdMap(goals);

    const buildGoalsTree = stratify<IGoalData>()
      .id((node) => node.id)
      .parentId((node) => {
        if (node.id === rootNode.id) {
          return null;
        }

        if (node.imported || !node.goal.parent) {
          return rootNode.id;
        }

        if (node.goal.parent.type === "goal") {
          return goalMap[node.goal.parent.id] ? node.goal.parent.id : rootNode.id;
        }

        const parentGoal = goals.find((goal) => goal.metrics.some((metric) => metric.id === node.goal.parent.id));
        return parentGoal ? parentGoal.id : rootNode.id;
      });

    const nodes = goals
      .map<IGoalData>((goal) => ({
        id: goal.id,
        goal,
        session: this.sessions[goal.sessionId] || this.noSession,
        imported: goal.type === "goal",
        published: goal.type === "goal",
      }))
      .concat([rootNode]);
    this.goalsTree = buildGoalsTree(nodes);

    removeGoalLeaves(this.goalsTree, [nonDraft, withNoName]);

    this.createSessionsStats();
    this.validateGoalsTree(this.goalsTree);
    this.selectAll(this.goalsTree);

    this.indicators.validating = { progress: true };
    this.syncGoalsCountStats().then(
      () => {
        delete this.indicators.validating;
        this.syncDraftGoalsCountStats();
        this.checkAllGoalsLimitReached();
      },
      (err) => {
        this.uiErrorHandlingService.handleModal(err);
      }
    );

    const trees: Record<string, HierarchyNode<IGoalData>[]> = {};
    if (this.goalsTree.children) {
      for (const node of this.goalsTree.children) {
        const sessionId = node.data.session.id;
        if (trees[sessionId]) {
          trees[sessionId].push(node);
        } else {
          trees[sessionId] = [node];
        }
      }
    }

    this.goalsPerSessionTrees = Object.values(trees).sort(bySessionEndDateDesc);
  }

  private validateGoalsTree(tree: HierarchyNode<IGoalData>) {
    if (!tree.children) {
      return;
    }

    for (const node of tree.children) {
      if (this.canSelectNode(node)) {
        this.checkMetricsLimitExceed(node);
      }

      // Do not overwrite the error for the child node that exceeded/reached the limit.
      // The method checkMetricsLimitExceed() will do it if needed.
      if (node.parent.data.error && !node.parent.data.metricsExceedLimit) {
        node.data.error = node.parent.data.error;
        continue;
      }

      const goalSession = node.data.session;

      if (goalSession === this.noSession) {
        node.data.error = this.noSession.title;
        continue;
      }

      if (goalSession.status != null && goalSession.status !== "open") {
        node.data.error = localize("arch_closed_session_status_descr");
        continue;
      }

      if (node.parent.data !== rootNode) {
        const parentSession = node.parent.data.session;
        if (!(goalSession.id === parentSession.id || goalSession.parentId === parentSession.id)) {
          node.data.error = localize("invalid_parent_obj_session");
          continue;
        }
      }

      const ownerError = this.validateOwners(node.data.goal);
      if (ownerError) {
        node.data.error = ownerError;
        continue;
      }

      this.validateGoalsTree(node);
    }
  }

  private validateOwners(goal: IGoalState) {
    if (goal.type !== "draftGoal") {
      return;
    }

    // remove after PATCH of whiteboards
    this.fixEmptyOwnerIds(goal);
    const sessionSettings = this.sessions[goal.sessionId]?.settings;

    const assignGoalPermissions = this.okrMethodologyFacade.getAssignGoalPermissionsSetting(sessionSettings);
    for (const id of goal.ownerIds) {
      if (!this.assignees[id]) {
        return localize("missing_owner");
      }

      if (!respectsAssignPermissions(this.assignees[id], assignGoalPermissions)) {
        return localize("owner_not_permitted_by_account_settings");
      }
    }

    const assignMetricPermissions = this.okrMethodologyFacade.getAssignMetricPermissions(sessionSettings);
    for (const metric of goal.metrics) {
      // remove after PATCH of whiteboards
      if (!metric.ownerIds) {
        metric.ownerIds = [metric.ownerId];
      }

      for (const id of metric.ownerIds) {
        if (!this.assignees[id]) {
          return localize("missing_owner");
        }

        if (!respectsAssignPermissions(this.assignees[id], assignMetricPermissions)) {
          return localize("owner_not_permitted_by_account_settings");
        }
      }
    }
  }

  private selectAll(tree: HierarchyNode<IGoalData>) {
    for (const child of tree.descendants()) {
      if (this.canSelectNode(child)) {
        child.data.selected = true;
      }
    }
  }

  private createBulkGoalRequest(draftGoalId: string): IBulkCreateGoalRequest {
    const draftGoal = this.whiteboardStateHolder.findShape<IDraftGoalState>(draftGoalId);
    const goal = this.shapeStateConverter.draftToGoal(draftGoal);
    delete goal.id;

    goal.ownerIds = this.assigneeService.getActiveAssigneesOnly(goal.ownerIds);
    for (const metric of goal.metrics) {
      delete metric.id;
    }

    return goal as IBulkCreateGoalRequest;
  }

  private fixEmptyOwnerIds(goal: IGoalState): void {
    if (!goal.ownerIds) {
      goal.ownerIds = [goal.ownerId];
    }
  }

  private lookAtForLimitReached(): boolean {
    return this.goalsTree.descendants().some((n) => n.data.goalLimitReached === true);
  }

  private isMetricsLimitExceeded(limit: number, count: number): boolean {
    if (count > limit) {
      return true;
    }
    return false;
  }

  private isGoalsLimitReached(goalsLimit: number, goalsCount: number, draftGoalsCount: number): boolean {
    const total = goalsCount + draftGoalsCount;
    if (total > goalsLimit) {
      return true;
    }
    return false;
  }

  private checkMetricsLimitExceed(node: HierarchyNode<IGoalData>): void {
    if (node.parent.data.metricsExceedLimit) {
      // The parent exceed the limit. This will set appropriate error to the child.
      node.data.error = localize("parent_objective_exceeds_limit_of_maximum_metrics");
      node.data.metricsExceedLimit = true;
    }

    const metricsCount = node.data.goal.metrics.length;
    const sessionSettings = this.sessions[node.data.goal.sessionId]?.settings;
    const metricsLimit = this.okrMethodologyFacade.getLimitSetting("metric", sessionSettings);
    const isLimitExceed = this.isMetricsLimitExceeded(metricsLimit, metricsCount);
    if (isLimitExceed) {
      const str1 = localize("method_champ_max_metrics_goal", { metricsLimit });
      const str2 = localize("goal_has_metrics", { "goal.metrics.length": metricsCount });
      node.data.error = str1 + " " + str2;
      node.data.metricsExceedLimit = true;
    }
  }

  private checkAllGoalsLimitReached(): void {
    for (const node of this.goalsTree.descendants()) {
      if (this.isRootNode(node) || !node.data.selected || node.data.error) {
        continue;
      }

      this.checkGoalLimitReached(node);
    }

    this.goalsLimitReached = this.lookAtForLimitReached();
  }

  private checkGoalLimitReached(node: HierarchyNode<IGoalData>): void {
    const sessionId = node.data.goal.sessionId;
    const foundSessionStats = this.findSessionStats(sessionId);
    const session = this.sessions[sessionId];
    const goalsLimit = this.okrMethodologyFacade.getLimitSetting("goal", session?.settings);

    for (const ownerId of node.data.goal.ownerIds) {
      const foundOwnerStats = this.findOwnerStats(ownerId, foundSessionStats.owners);

      const isLimitReached = this.isGoalsLimitReached(goalsLimit, foundOwnerStats.goalsCount, foundOwnerStats.draftGoalsCount);

      if (isLimitReached) {
        node.data.goalLimitReached = true;
        //  We don't need to checks the other owners
        return;
      }
    }
  }

  private findSessionStats(sessionId): ISessionsStats {
    return this.sessionsStats.find((s) => {
      return s.sessionId === sessionId;
    });
  }

  private findOwnerStats(ownerId: string, ownerList: IOwnersStats[]): IOwnersStats {
    return ownerList.find((o) => {
      return o.ownerId === ownerId;
    });
  }

  private isRootNode(node): boolean {
    return node.id === rootNode.id;
  }

  private createSessionsStats(): void {
    for (const node of this.goalsTree.descendants()) {
      if (!this.canSelectNode(node)) {
        continue;
      }

      // remove after PATCH of whiteboards
      this.fixEmptyOwnerIds(node.data.goal);

      if (!this.sessionsStats.some((o) => o.sessionId === node.data.goal.sessionId)) {
        const sessionStats: ISessionsStats = {
          sessionId: node.data.goal.sessionId,
          ownerIds: node.data.goal.ownerIds,
          owners: [],
        };

        this.sessionsStats.push(sessionStats);
      } else {
        const currSessionStats = this.findSessionStats(node.data.goal.sessionId);

        // Get only unique owners we do not need a duplicates
        const difference = node.data.goal.ownerIds.filter((x) => !currSessionStats.ownerIds.includes(x));
        if (difference.length > 0) {
          currSessionStats.ownerIds.push(...difference);
        }
      }

      const foundSessionStats = this.findSessionStats(node.data.goal.sessionId);
      for (const ownerId of foundSessionStats.ownerIds) {
        if (!this.findOwnerStats(ownerId, foundSessionStats.owners)) {
          const owner: IOwnersStats = {
            ownerId: ownerId,
            goalsCount: 0,
            draftGoalsCount: 0,
          };

          foundSessionStats.owners.push(owner);
        }
      }
    }
  }

  private syncGoalsCountStats(): IPromise<void[]> {
    const promises: IPromise<void>[] = [];
    for (const sessionStats of this.sessionsStats) {
      const assigneeIds = sessionStats.ownerIds.map((id) => id).toString();
      const currPromise = this.planningSessionService.statsAssignees(sessionStats.sessionId, { assigneeIds }).then((response) => {
        response.forEach((item) => {
          const foundOwnerStats = this.findOwnerStats(item.assigneeId, sessionStats.owners);
          foundOwnerStats.goalsCount = item.planningSessionStats.goalCount;
        });
      });
      promises.push(currPromise);
    }
    return this.$q.all(promises);
  }

  private invalidateGoalLimitReached(): void {
    for (const node of this.goalsTree.descendants()) {
      if (this.isRootNode(node)) {
        continue;
      }

      node.data.goalLimitReached = false;
    }
  }

  private invalidateDraftGoalsCountStats(): void {
    for (const sessionStats of this.sessionsStats) {
      const foundSessionStats = this.findSessionStats(sessionStats.sessionId);
      for (const ownerId of foundSessionStats.ownerIds) {
        const foundOwnerStats = this.findOwnerStats(ownerId, foundSessionStats.owners);
        foundOwnerStats.draftGoalsCount = 0;
      }
    }
  }

  private syncDraftGoalsCountStats(): void {
    this.invalidateDraftGoalsCountStats();

    for (const node of this.goalsTree.descendants()) {
      if (this.isRootNode(node) || !node.data.selected || node.data.error) {
        continue;
      }

      const foundSessionStats = this.findSessionStats(node.data.goal.sessionId);

      for (const ownerId of node.data.goal.ownerIds) {
        const foundOwnerStats = this.findOwnerStats(ownerId, foundSessionStats.owners);

        // Adding this node as count to owner stats
        foundOwnerStats.draftGoalsCount++;
      }
    }
  }
}

export class PublishDraftsModal {
  static $inject = ["$uibModal"];

  constructor(private $uibModal: IModalService) {}

  open(whiteboardId: string): void {
    this.$uibModal.open({
      component: "publishDrafts",
      resolve: {
        whiteboardId: () => whiteboardId,
      },
      windowClass: "medium publish-drafts",
      backdrop: false,
    });
  }
}

export const PublishDraftsComponent: IComponentOptions = {
  controller: PublishDraftsComponentCtrl,
  templateUrl: require("./publish-drafts.jade"),
  bindings: {
    resolve: "<",
    close: "&",
    dismiss: "&",
  },
};
